/**
 * Templates — one-click, production-grade creative recipes (E13, v0.20.0).
 *
 * A template is a curated (engine × engineered prompt × params) recipe with
 * typed user inputs: drop a product photo, get an ad-grade scene; type a
 * brand name, get an Arabic sign video on a verified engine. The prompt is
 * PLATFORM IP: it lives server-side in the doc and never crosses the API
 * boundary — clients get a projection (`TemplateDto`) without it.
 *
 * Same registry philosophy as model manifests (ADR-002): templates are
 * zod-validated docs in a DB table; category rails, inputs, pricing and
 * routing are ALL doc-driven — shipping a new template is a data change.
 * Credits are never stored on the doc: the API computes them by building
 * the template's spec and running the shared estimator, so template price
 * always equals what the job would charge (ADR-008).
 */
import { z } from 'zod';
import { generationSpecSchema } from './spec.js';
export const TEMPLATE_CATEGORIES = [
    { key: 'marketing', en: 'Marketing', ar: 'تسويق' },
    { key: 'products', en: 'Products', ar: 'منتجات' },
    { key: 'fashion', en: 'Fashion', ar: 'أزياء' },
    { key: 'jewelry', en: 'Jewelry', ar: 'مجوهرات' },
    { key: 'food', en: 'Food', ar: 'أطعمة' },
    { key: 'occasions', en: 'Occasions', ar: 'مناسبات' },
];
const localizedSchema = z.object({ en: z.string().min(1), ar: z.string().min(1) }).strict();
const templateInputSchema = z
    .object({
    key: z.string().regex(/^[a-z][a-zA-Z0-9]*$/),
    type: z.enum(['image', 'text']),
    required: z.boolean().default(true),
    label: localizedSchema,
    /** Hint under the control, e.g. "clean packshot works best". */
    help: localizedSchema.optional(),
    /** text inputs only — interpolation cap (default 120). */
    maxChars: z.number().int().min(1).max(300).optional(),
    /** text inputs only — example value shown as placeholder. */
    example: localizedSchema.optional(),
})
    .strict();
export const templateSchema = z
    .object({
    /** Slug id, stable forever (used in URLs). */
    id: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
    category: z.enum(TEMPLATE_CATEGORIES.map((c) => c.key)),
    /** Chip row grouping inside the category (label pair inline — doc-driven). */
    subcategory: z.object({ key: z.string(), en: z.string(), ar: z.string() }).strict(),
    modality: z.enum(['image', 'video']),
    name: localizedSchema,
    description: localizedSchema,
    /** Typed user inputs. At most ONE image input (maps to the source frame). */
    inputs: z.array(templateInputSchema).max(4),
    /** Engine binding — modelId + the params the recipe pins. */
    engine: z
        .object({
        modelId: z.string(),
        params: z
            .object({
            mode: z.enum(['t2i', 'i2i', 't2v', 'i2v']).optional(),
            aspect: z.string().optional(),
            resolution: z.string().optional(),
            durationSec: z.number().positive().optional(),
            audio: z.boolean().optional(),
            batch: z.number().int().min(1).max(8).optional(),
        })
            .strict()
            .default({}),
    })
        .strict(),
    /** SERVER-SIDE ONLY. {{key}} placeholders resolve from text inputs.
     * Speech templates: this is the PRESENTER scene direction (it maps to
     * params.presenterPrompt, not params.prompt). */
    prompt: z
        .object({
        template: z.string().min(1).max(4000),
        negative: z.string().max(2000).optional(),
    })
        .strict(),
    /** Spokesperson recipes: which text input is the SPOKEN script (fed to
     * TTS verbatim — never interpolated into any image/video prompt), the
     * dialect-tuned voice, and the avatar's acting direction. */
    speech: z
        .object({
        scriptKey: z.string(),
        voiceId: z.string(),
        motion: z.string().max(1500).optional(),
    })
        .strict()
        .optional(),
    /** Public sample media (static web assets, e.g. /templates/x.webp|.mp4). */
    media: z
        .object({
        cover: z.string(),
        /** mp4 covers need a poster for instant paint. */
        poster: z.string().optional(),
        /** input-based templates: the before/after pair behind the slider. */
        before: z.string().optional(),
        after: z.string().optional(),
    })
        .strict(),
    /** SERVER-SIDE ONLY — how the committed sample was produced (repro). */
    sample: z
        .object({
        vars: z.record(z.string(), z.string()).optional(),
        beforePrompt: z.string().optional(),
    })
        .strict()
        .optional(),
    badge: z.enum(['NEW', 'TOP', 'ARABIC']).nullable().default(null),
    order: z.number().int().default(100),
    active: z.boolean().default(true),
    version: z.string(),
})
    .strict()
    .refine((t) => t.inputs.filter((i) => i.type === 'image').length <= 1, {
    message: 'at most one image input per template',
    path: ['inputs'],
})
    .refine((t) => {
    const keys = new Set(t.inputs.map((i) => i.key));
    const placeholders = [...t.prompt.template.matchAll(/\{\{([a-zA-Z0-9]+)\}\}/g)].map((m) => m[1] ?? '');
    return placeholders.every((p) => keys.has(p));
}, { message: 'every {{placeholder}} must match a declared text input', path: ['prompt', 'template'] })
    .refine((t) => !t.speech || t.inputs.some((i) => i.type === 'text' && i.key === t.speech.scriptKey), {
    message: 'speech.scriptKey must reference a declared text input',
    path: ['speech', 'scriptKey'],
});
export function toTemplateDto(doc, credits) {
    return {
        id: doc.id,
        category: doc.category,
        subcategory: doc.subcategory,
        modality: doc.modality,
        name: doc.name,
        description: doc.description,
        inputs: doc.inputs,
        modelId: doc.engine.modelId,
        media: doc.media,
        badge: doc.badge,
        order: doc.order,
        credits,
    };
}
/**
 * Interpolate user text vars into the prompt template. Values are sanitized
 * (braces stripped so users can't splice placeholders, whitespace collapsed,
 * length-capped) — prompt-injection hardening at the boundary.
 */
export function renderTemplatePrompt(doc, vars) {
    let out = doc.prompt.template;
    for (const input of doc.inputs) {
        if (input.type !== 'text')
            continue;
        const raw = vars[input.key] ?? '';
        const value = raw
            .replaceAll(/[{}]/g, '')
            .replaceAll(/\s+/g, ' ')
            .trim()
            .slice(0, input.maxChars ?? 120);
        out = out.replaceAll(`{{${input.key}}}`, value);
    }
    return out;
}
/** Validate the caller's inputs against the template's declared inputs. */
export function validateTemplateInputs(doc, inputs) {
    const issues = [];
    for (const input of doc.inputs) {
        if (input.type === 'image' && input.required && !inputs.imageAssetId) {
            issues.push({ pointer: `/inputs/${input.key}`, message: 'source image is required for this template' });
        }
        if (input.type === 'text' && input.required && !inputs.vars?.[input.key]?.trim()) {
            issues.push({ pointer: `/inputs/${input.key}`, message: 'this field is required' });
        }
    }
    const imageInput = doc.inputs.some((i) => i.type === 'image');
    if (!imageInput && inputs.imageAssetId) {
        issues.push({ pointer: '/inputs/image', message: 'this template does not take a source image' });
    }
    return issues;
}
/**
 * Build the real GenerationSpec for a template run. The image input maps to
 * the source frame (i2i for image engines, i2v for video engines); mode is
 * derived when the recipe didn't pin one. Validated against the spec schema
 * so a bad doc can never produce an unparseable job.
 */
export function buildTemplateSpec(doc, inputs, locale = 'en') {
    const hasImage = !!inputs.imageAssetId;
    const mode = doc.engine.params.mode ??
        (doc.modality === 'image' ? (hasImage ? 'i2i' : 't2i') : hasImage ? 'i2v' : 't2v');
    // Spokesperson recipes: the script rides params.text VERBATIM (TTS input,
    // never mixed into a visual prompt — ADR-009), the doc prompt becomes the
    // presenter staging direction, and the engine prompt is the acting note.
    const speech = doc.speech;
    const scriptInput = speech ? doc.inputs.find((i) => i.key === speech.scriptKey) : undefined;
    const script = speech
        ? (inputs.vars?.[speech.scriptKey] ?? '').replaceAll(/\s+/g, ' ').trim().slice(0, scriptInput?.maxChars ?? 300)
        : undefined;
    return generationSpecSchema.parse({
        modelId: doc.engine.modelId,
        routeMode: 'explicit',
        modality: doc.modality,
        locale,
        params: {
            ...(speech
                ? {
                    prompt: speech.motion ?? 'The person speaks directly to the camera with natural hand gestures, warm confident energy and steady eye contact.',
                    presenterPrompt: renderTemplatePrompt(doc, inputs.vars ?? {}),
                    text: script,
                    voice: speech.voiceId,
                }
                : { prompt: renderTemplatePrompt(doc, inputs.vars ?? {}) }),
            ...(doc.prompt.negative ? { negativePrompt: doc.prompt.negative } : {}),
            mode,
            ...(inputs.imageAssetId ? { startFrameAssetId: inputs.imageAssetId } : {}),
            ...(doc.engine.params.aspect ? { aspect: doc.engine.params.aspect } : {}),
            ...(doc.engine.params.resolution ? { resolution: doc.engine.params.resolution } : {}),
            ...(doc.engine.params.durationSec ? { durationSec: doc.engine.params.durationSec } : {}),
            ...(doc.engine.params.audio !== undefined ? { audio: doc.engine.params.audio } : {}),
            ...(doc.engine.params.batch ? { batch: doc.engine.params.batch } : {}),
        },
    });
}
//# sourceMappingURL=template.js.map